import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { useNavigate, useParams, Link } from "react-router-dom";
import { RootState, AppDispatch } from "../../../store";
import { fetchVentures } from "../../../store/slices/venturesSlice";
import Card from "../../../components/base-components/Card";
import Loader from "../../../components/base-components/Loader";
import ErrorMessage from "../../../components/base-components/Error";
import NoData from "../../../components/base-components/NoData";

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};

const ExploreMoreVentures: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id: currentVentureId } = useParams<{ id: string }>();

  const { ventures, status, error } = useSelector(
    (state: RootState) => state.ventures
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);

  const itemsPerPage = 3;
  const baseUrl =
    process.env.REACT_APP_STRAPI_IMAGES_URL?.replace(/\/$/, "") || "";

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchVentures());
    }
  }, [dispatch, status]);

  if (status === "loading") {
    return <Loader />;
  }

  if (status === "failed") {
    return <ErrorMessage message={error} />;
  }

  if (!ventures || ventures.length === 0) {
    return <NoData message="No ventures found" />;
  }

  const filteredVentures = ventures.filter(
    (venture) => venture.id.toString() !== currentVentureId
  );

  const galleryItems = filteredVentures.map((venture) => ({
    id: venture.id,
    title: venture.attributes.title,
    tags: venture.attributes.tags,
    description:
      venture.attributes.shortDescription || "Description not available.",
    imageUrl: venture.attributes.logo.data?.attributes.formats.small?.url
      ? `${baseUrl}${venture.attributes.logo.data.attributes.formats.small?.url}`
      : `${baseUrl}${venture.attributes.logo.data?.attributes.url}`,
  }));

  const totalPages = Math.ceil(galleryItems.length / itemsPerPage);
  const paginatedItems = galleryItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setActiveCardId(null);
  };

  const handleCardClick = (id: number) => {
    if (activeCardId === id) {
      navigate(`/ventures/${id}`);
    } else {
      setActiveCardId(id);
    }
  };

  return (
    <div className="w-full mb-[7rem]">
      <div className="flex justify-between flex-wrap mb-[2.4rem]">
        <h4 className="mr-[1.2rem] whitespace-nowrap mb-[2rem] md:mb-0">
          Explore More Ventures
        </h4>
        <Link
          to="/ventures"
          className="bg-primary flex items-center border border-transparent text-black px-[1.2rem] py-[0.4rem] rounded-[0.5rem] h-[5rem] hover:bg-yellow-400"
        >
          <p className="whitespace-nowrap p-[1rem]">View All</p>
        </Link>
      </div>

      <motion.section
        className="h-auto"
        initial="hidden"
        animate="visible"
        transition={{ staggerChildren: 0.2 }}
      >
        <div
          className="grid gap-[1.6rem] justify-center items-center"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(35rem, 1fr))",
          }}
        >
          {paginatedItems.map((item) => (
            <motion.div
              key={item.id}
              layout
              className="relative rounded-[0.5rem] shadow-lg cursor-pointer overflow-hidden group"
              onClick={() => handleCardClick(item.id)}
              onMouseEnter={() => setActiveCardId(item.id)}
              onMouseLeave={() => setActiveCardId(null)}
              variants={cardVariants}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Card
                key={item.id}
                id={item.id}
                image={item.imageUrl}
                title={item.title}
                tags={item.tags}
                description={item.description}
              />
            </motion.div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center gap-4 mt-[2rem]">
            <p className="text-black">
              Page {currentPage} of {totalPages}
            </p>

            <div className="flex">
              {currentPage !== 1 && (
                <button
                  className="mr-[1.2rem]"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <rect
                      x="40"
                      y="40"
                      width="40"
                      height="40"
                      rx="5"
                      transform="rotate(-180 40 40)"
                      fill="white"
                    />
                    <path
                      d="M25 10L15 20L25 30"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              {currentPage !== totalPages && (
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <rect width="40" height="40" rx="5" fill="white" />
                    <path
                      d="M15 30L25 20L15 10"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </motion.section>
    </div>
  );
};

export default ExploreMoreVentures;
